/**
 * LiveStreamingStateViews — the non-video surface chrome of the live page.
 *
 * A small variant-driven component (the `VideoCallStateView` idiom) that renders
 * one of the live page's overlay states, kept out of `LiveStreamingPage.tsx`
 * (Task 14) so the page stays under the size threshold (`presentation.md` —
 * component size). Each variant mirrors one Flutter widget in
 * `live_streaming_screen.dart`:
 *
 *   - `loading`        → `loadingWidget` (`:1178-1191`) — visible while
 *                        `_isOpeningChannel`: the broadcaster's circular profile
 *                        avatar centred on the surface (`CircleBorderAvatar` of
 *                        `generateUserProfileImageUrl(broadcasterFirebaseId)`).
 *   - `renewingToken`  → `renewingTokenWidget` (`:1193-1210`) — visible while
 *                        `_isRenewingLiveStreamingToken`: centred localized text.
 *   - `streamingEnded` → `streamingEndedWidget` (`:1212-1226`) — visible while
 *                        `_liveStreamingEnded`: centred localized text.
 *   - `topOverlay`     → `topOverlay` (`:877-890`) — a top gradient scrim band,
 *                        hidden by the page while `clearScreen`.
 *
 * Visibility gating (which variant to render, and when) lives on the page (Task
 * 14), mirroring the Flutter `Visibility(visible: …)` / `_clearScreen` guards —
 * this component only renders the passed variant.
 *
 * Theme/responsive/l10n + `qaAttr` are mandatory (`presentation.md`). The two
 * text variants are static (no interpolation), so this component reads their
 * copy from `l10n.community.liveStreamingPage` directly (the sibling
 * `LiveStreamingVideoView` / `LiveStreamingControls` idiom) rather than taking a
 * pre-localized `message` prop. `data-qa-status` carries the variant.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * Which non-video state view to render. The values double as the
 * `data-qa-status` token, so QA can `wait_for` a known state.
 */
export type LiveStreamingStateVariant =
  | 'loading'
  | 'renewingToken'
  | 'streamingEnded'
  | 'topOverlay';

export interface LiveStreamingStateViewsProps {
  variant: LiveStreamingStateVariant;
  /**
   * The broadcaster's firebase id — used to render the loading avatar (Flutter
   * `generateUserProfileImageUrl(broadcasterFirebaseId)`, `:1185-1187`). Only
   * read for the `loading` variant.
   */
  broadcasterFirebaseId?: string;
}

export const LiveStreamingStateViews: React.FC<LiveStreamingStateViewsProps> = ({
  variant,
  broadcasterFirebaseId,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const copy = l10n.community.liveStreamingPage;

  // Top gradient scrim band (Flutter `topOverlay`, `:877-890`): a full-width band
  // pinned to the top, `viewPadding.top + 80` tall, painted with the
  // `topOverlayDecorationLiveStreaming` gradient. Non-interactive so it never
  // eats a clear-screen tap.
  if (variant === 'topOverlay') {
    return (
      <div
        {...qaAttr({ id: 'live-streaming-state', status: variant })}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: `calc(${responsive.safeArea.top} + ${responsive.liveStreaming.topOverlayHeight}px)`,
          background: theme.liveStreamingTopOverlay,
          pointerEvents: 'none',
        }}
      />
    );
  }

  // Loading view (Flutter `loadingWidget`, `:1178-1191`): the broadcaster's
  // circular avatar centred on the surface while the channel opens.
  if (variant === 'loading') {
    return (
      <div
        {...qaAttr({ id: 'live-streaming-state', status: variant })}
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        {broadcasterFirebaseId && (
          <CachedImage
            src={UserUtils.generateUserProfileImageUrl(broadcasterFirebaseId)}
            alt=""
            style={{
              width: responsive.avatar.xlarge,
              height: responsive.avatar.xlarge,
              borderRadius: responsive.borderRadius.round,
              objectFit: 'cover',
            }}
          />
        )}
      </div>
    );
  }

  // The two centred text overlays (`renewingTokenWidget` `:1193-1210` /
  // `streamingEndedWidget` `:1212-1226`): a single centred, localized text block.
  const message =
    variant === 'renewingToken' ? copy.renewingToken : copy.creatorLeft;

  return (
    <div
      {...qaAttr({ id: 'live-streaming-state', status: variant })}
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: responsive.spacing['3xl'],
        textAlign: 'center',
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          color: theme.white,
          fontSize: responsive.font.lg,
          textAlign: 'center',
        }}
      >
        {message}
      </span>
    </div>
  );
};
